/**
 * SpeedRun mode: layouts in normalized canvas coords (x: 0–1 * width, y: 0–1 * height).
 * Official levels load from `/api/speedrun-levels` into `runtimeLevels`.
 */

export type NormSeg = { ax: number; ay: number; bx: number; by: number };

export const SPEEDRUN_TOGGLE_GROUP_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const;
export type SpeedrunToggleGroup = (typeof SPEEDRUN_TOGGLE_GROUP_IDS)[number];

export const SPEEDRUN_POINT_TYPES = ["circle", "triangle", "square"] as const;
export type SpeedrunPointType = (typeof SPEEDRUN_POINT_TYPES)[number];

function parsePointTypeField(raw: unknown): SpeedrunPointType | undefined {
    if (raw === "circle" || raw === "triangle" || raw === "square") return raw;
    if (raw === "squere") return "square";
    return undefined;
}

/** Point shape for collectibles (default circle). */
export function speedrunPointTypeOf(pt: { pointType?: SpeedrunPointType }): SpeedrunPointType {
    return parsePointTypeField(pt.pointType) ?? "circle";
}

/** Toggle lasers tied to a point shape within the same numeric group. */
export function speedrunTogglePointTypeOf(obj: { togglePointType?: SpeedrunPointType }): SpeedrunPointType {
    return parsePointTypeField(obj.togglePointType) ?? "circle";
}

export function drawSpeedrunPointShape(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    radius: number,
    pointType: SpeedrunPointType,
    rotationDeg = 0,
): void {
    ctx.save();
    ctx.translate(cx, cy);
    if (rotationDeg) ctx.rotate((rotationDeg * Math.PI) / 180);
    ctx.translate(-cx, -cy);
    if (pointType === "triangle") {
        const halfW = radius * 1.55;
        const topY = cy - radius * 1.05;
        const botY = cy + radius * 0.95;
        ctx.beginPath();
        ctx.moveTo(cx, topY);
        ctx.lineTo(cx + halfW, botY);
        ctx.lineTo(cx - halfW, botY);
        ctx.closePath();
        ctx.fill();
        return;
    }
    if (pointType === "square") {
        const side = radius * 1.65;
        ctx.fillRect(cx - side / 2, cy - side / 2, side, side);
        return;
    }
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
}

/** Base RGB (0–255) for groups 1–15 on points / toggle walls (avoids pure black and laser red). */
export function speedrunGroupRgb(g: SpeedrunToggleGroup): { r: number; g: number; b: number } {
    const m: Record<SpeedrunToggleGroup, [number, number, number]> = {
        1: [70, 130, 255],
        2: [55, 195, 95],
        3: [255, 155, 65],
        4: [165, 95, 235],
        5: [115, 215, 255],
        6: [255, 135, 205],
        7: [245, 245, 252],
        8: [25, 185, 165],
        9: [155, 158, 175],
        10: [225, 55, 205],
        11: [175, 195, 85],
        12: [145, 95, 60],
        13: [255, 155, 145],
        14: [255, 105, 25],
        15: [35, 105, 65],
    };
    const v = m[g];
    return { r: v[0], g: v[1], b: v[2] };
}

function parseToggleGroupField(raw: unknown): SpeedrunToggleGroup | undefined {
    if (raw === "A" || raw === "a") return 1;
    if (raw === "B" || raw === "b") return 2;
    if (raw === "C" || raw === "c") return 3;
    const n = typeof raw === "number" ? raw : Math.floor(Number(raw));
    if (Number.isInteger(n) && n >= 1 && n <= 15) return n as SpeedrunToggleGroup;
    return undefined;
}

/** Optional wall motion / rotation (editor + runtime). */
export type WallSegDef = NormSeg & {
    id?: string;
    /** When true: matte collision wall (no death); cursor slides along it. */
    solidWall?: boolean;
    /** Laser stroke thickness multiplier (1 = default). */
    lineHalfMul?: number;
    pathMove?: OscPathMove;
    move?: { tax: number; tay: number; tbx: number; tby: number; outS: number; backS: number };
    /** Oscillation uses deg as sweep amplitude; infinite uses periodS as seconds per full turn. */
    rotate?: { deg: number; periodS: number; infinite?: boolean; spinDir?: 1 | -1 };
    /** When all points of this group are collected, this wall is disabled (SpeedRun). */
    toggleGroup?: SpeedrunToggleGroup;
    /** Which point shape in `toggleGroup` must all be collected to disable this wall. */
    togglePointType?: SpeedrunPointType;
};

/** Back-and-forth oscillation toward a target point in normalized coordinates. */
export type OscPointMove = { tnx: number; tny: number; outS: number; backS: number };

/** Path along centroids: rest → anchors[0] → …; if `loop`, one leg returns to rest then repeats; otherwise it reverses along the same path. */
export type OscPathMove = {
    restNx: number;
    restNy: number;
    anchors: { nx: number; ny: number }[];
    loop: boolean;
    legOutS: number;
    legBackS: number;
};

/** Moving laser oscillation: target Y and horizontal center cx. */
export type MovingLaserOscMove = { ty: number; tcx: number; outS: number; backS: number };

export type MovingLaserDef = {
    y: number;
    halfLen: number;
    amp: number;
    speed: number;
    phase: number;
    /** Stroke thickness multiplier (1 = default). */
    lineHalfMul?: number;
    /** Horizontal oscillation center in 0..1; default 0.5. */
    cx?: number;
    pathMove?: OscPathMove;
    move?: MovingLaserOscMove;
    /** When every point in the group is collected, this laser stops colliding / drawing (like `toggleGroup` on segments). */
    toggleGroup?: SpeedrunToggleGroup;
    /** Which point shape in `toggleGroup` must all be collected to disable this laser. */
    togglePointType?: SpeedrunPointType;
};

/** Laser half-thickness in px = `SPEEDRUN_LASER_HALF_BASE_PX * lineHalfMul`. */
export const SPEEDRUN_LASER_HALF_BASE_PX = 5;

export function speedrunLaserHalfPxForWall(s: WallSegDef, layoutScale = 1): number {
    const mul = clamp(Number(s.lineHalfMul ?? 1), 0.25, 5);
    const siz = clamp(Number(layoutScale) || 1, 0.25, 4);
    return SPEEDRUN_LASER_HALF_BASE_PX * mul * siz;
}

/** True when this segment uses oscillation / path / rotate motion at runtime (non-static pose over time). */
export function wallSolidAnimatesAtRuntime(sdef: WallSegDef): boolean {
    const mv = sdef.move;
    if (mv && mv.outS > 0.01 && mv.backS > 0.01) return true;
    const pm = sdef.pathMove;
    if (pm && pm.anchors && pm.anchors.length > 0) return true;
    if (sdef.rotate && sdef.rotate.periodS > 0.05) return true;
    return false;
}

export function speedrunLaserHalfPxForMoving(m: MovingLaserDef, layoutScale = 1): number {
    const mul = clamp(Number(m.lineHalfMul ?? 1), 0.25, 5);
    const siz = clamp(Number(layoutScale) || 1, 0.25, 4);
    return SPEEDRUN_LASER_HALF_BASE_PX * mul * siz;
}

export type SpeedrunPointDef = {
    nx: number;
    ny: number;
    /** Point scale and pickup radius scale (1 = default). */
    radiusMul?: number;
    /** Group 1–15 = optional color; omitted = yellow (required for goal if present). */
    group?: SpeedrunToggleGroup;
    /** Collectible shape; toggles only lasers with the same group + `togglePointType`. */
    pointType?: SpeedrunPointType;
    rotationDeg?: number;
    pathMove?: OscPathMove;
    move?: OscPointMove;
};

export type SpeedrunGoalDef = {
    nx: number;
    ny: number;
    nHalf: number;
    rotationDeg?: number;
    pathMove?: OscPathMove;
    move?: OscPointMove;
};

export type SpeedrunPlaytestCheckpointDef = {
    nx: number;
    ny: number;
    /** Visual / hit scale (1 = default). */
    sizeMul?: number;
    rotationDeg?: number;
    pathMove?: OscPathMove;
    move?: OscPointMove;
};

/** Alias — published levels use `checkpoint`; legacy saves may use `playtestCheckpoint`. */
export type SpeedrunCheckpointDef = SpeedrunPlaytestCheckpointDef;

/** Read gameplay checkpoints from level def (supports legacy single `checkpoint`). */
export function speedrunLevelCheckpoints(def: SpeedrunLevelDef): SpeedrunCheckpointDef[] {
    if (def.checkpoints?.length) return def.checkpoints;
    const legacy = def.checkpoint;
    if (legacy) return [legacy];
    return [];
}

/** Editor / playtest-only start override (max one). */
export function speedrunLevelTemporalSpawn(def: SpeedrunLevelDef): SpeedrunPlaytestCheckpointDef | undefined {
    return def.temporalSpawn ?? def.playtestCheckpoint;
}

/** Base checkpoint radius in px before layout scale and `sizeMul`. */
export function speedrunCheckpointBaseRadiusPx(layoutScale = 1): number {
    const ls = clamp(Number(layoutScale) || 1, 0.25, 4);
    return Math.max(8 * ls, Math.min(20 * ls, 11 * ls));
}

export function speedrunCheckpointRadiusPx(sizeMul: number | undefined, layoutScale = 1): number {
    const mul = clamp(Number(sizeMul ?? 1), 0.45, 2.5);
    return speedrunCheckpointBaseRadiusPx(layoutScale) * mul;
}

/** Draw a symmetric deltoid (kite) for checkpoint collectibles. */
export function drawSpeedrunCheckpointDeltoid(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    radius: number,
    activated: boolean,
    rotationDeg = 0,
): void {
    ctx.save();
    ctx.translate(cx, cy);
    if (rotationDeg) ctx.rotate((rotationDeg * Math.PI) / 180);
    ctx.translate(-cx, -cy);
    const r = radius;
    const topY = cy - r * 1.08;
    const midY = cy + r * 0.08;
    const botY = cy + r * 0.82;
    const wing = r * 0.92;
    const dull = "#3d6b52";
    const dullEdge = "#2a4d3a";
    const bright = "#5dff96";
    const brightEdge = "#2ecc71";
    const fill = activated ? bright : dull;
    const edge = activated ? brightEdge : dullEdge;
    ctx.beginPath();
    ctx.moveTo(cx, topY);
    ctx.lineTo(cx + wing, midY);
    ctx.lineTo(cx, botY);
    ctx.lineTo(cx - wing, midY);
    ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.strokeStyle = edge;
    ctx.lineWidth = Math.max(1.2, r * 0.1);
    ctx.stroke();
    if (activated) {
        ctx.strokeStyle = "rgba(200,255,220,0.75)";
        ctx.lineWidth = Math.max(1, r * 0.07);
        ctx.beginPath();
        ctx.moveTo(cx, topY + r * 0.12);
        ctx.lineTo(cx, botY - r * 0.1);
        ctx.stroke();
    }
    ctx.restore();
}

/** @deprecated Use drawSpeedrunCheckpointDeltoid */
export const drawSpeedrunCheckpointEmerald = drawSpeedrunCheckpointDeltoid;

/** Rotate-wheel handle for editor (handle at top when rotation = 0°). */
export function speedrunRotateWheelHandlePx(
    cx: number,
    cy: number,
    wheelR: number,
    rotationDeg = 0,
): { cx: number; cy: number; wheelR: number; hx: number; hy: number } {
    const rad = (rotationDeg * Math.PI) / 180 - Math.PI / 2;
    return {
        cx,
        cy,
        wheelR,
        hx: cx + Math.cos(rad) * wheelR,
        hy: cy + Math.sin(rad) * wheelR,
    };
}

/** Direction the wind pushes the cursor (screen coords: +y is down). */
export type SpeedrunWindDir = "up" | "down" | "left" | "right";

/** Axis-aligned zone in normalized coords; `(nx, ny)` is top-left, `nw`×`nh` size. */
export type SpeedrunWindDef = {
    id?: string;
    nx: number;
    ny: number;
    nw: number;
    nh: number;
    dir: SpeedrunWindDir;
    /** Push intensity (default 1). Typical editor range ~0.15–50. */
    strength?: number;
};

/** Linked portal endpoint — line segment in normalized coords; pair A↔B per `group` (1–10). */
export type SpeedrunPortalSide = "a" | "b";

export type SpeedrunPortalDef = {
    id?: string;
    /** Portal pair id 1–10; at most one A and one B per group. */
    group: number;
    side: SpeedrunPortalSide;
    ax: number;
    ay: number;
    bx: number;
    by: number;
};

/** Side A / side B colors per portal group (group 1 = blue / orange). */
export const SPEEDRUN_PORTAL_GROUP_COLORS: ReadonlyArray<{ a: string; b: string }> = [
    { a: "#4488ff", b: "#ff8844" },
    { a: "#44dd88", b: "#dd44aa" },
    { a: "#44ddff", b: "#ffdd44" },
    { a: "#9966ff", b: "#aaff44" },
    { a: "#ff5566", b: "#44ccbb" },
    { a: "#ff66cc", b: "#ffcc44" },
    { a: "#6677ff", b: "#ff8866" },
    { a: "#33cc99", b: "#cc66ff" },
    { a: "#55bbff", b: "#ffaa33" },
    { a: "#ff7799", b: "#66ffcc" },
];

export function speedrunPortalColor(group: number, side: SpeedrunPortalSide): string {
    const g = clamp(Math.round(group), 1, 10);
    const pair = SPEEDRUN_PORTAL_GROUP_COLORS[g - 1];
    return side === "a" ? pair.a : pair.b;
}

export function speedrunPortalHalfThicknessPx(lineScale = 1): number {
    return Math.max(4, 7 * lineScale);
}

export function speedrunPortalPairmate(
    portals: SpeedrunPortalDef[],
    group: number,
    side: SpeedrunPortalSide,
): SpeedrunPortalDef | null {
    const other: SpeedrunPortalSide = side === "a" ? "b" : "a";
    return portals.find((p) => p.group === group && p.side === other) ?? null;
}

/** Parameter t ∈ [0,1] along segment (ax,ay)→(bx,by) for point (px,py) in normalized space. */
export function speedrunPortalParamOnSeg(
    px: number,
    py: number,
    ax: number,
    ay: number,
    bx: number,
    by: number,
): number {
    const dx = bx - ax;
    const dy = by - ay;
    const len2 = dx * dx + dy * dy;
    if (len2 < 1e-12) return 0.5;
    return clamp(((px - ax) * dx + (py - ay) * dy) / len2, 0, 1);
}

export type SpeedrunPortalFramePx = {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    ux: number;
    uy: number;
    nx: number;
    ny: number;
    len: number;
};

export function speedrunPortalFramePx(portal: SpeedrunPortalDef, rw: number, rh: number): SpeedrunPortalFramePx {
    const x1 = portal.ax * rw;
    const y1 = portal.ay * rh;
    const x2 = portal.bx * rw;
    const y2 = portal.by * rh;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    return { x1, y1, x2, y2, ux, uy, nx: -uy, ny: ux, len };
}

export type SpeedrunPortalClosestPx = {
    /** Unclamped projection along portal segment. */
    tRaw: number;
    t: number;
    qx: number;
    qy: number;
    /** Signed perpendicular distance to portal centerline (+n side). */
    perp: number;
    onSegment: boolean;
    frame: SpeedrunPortalFramePx;
};

export function speedrunPortalClosestPx(
    portal: SpeedrunPortalDef,
    px: number,
    py: number,
    rw: number,
    rh: number,
    segmentPad = 0.12,
): SpeedrunPortalClosestPx {
    const frame = speedrunPortalFramePx(portal, rw, rh);
    const tRaw = ((px - frame.x1) * frame.ux + (py - frame.y1) * frame.uy) / frame.len;
    const onSegment = tRaw >= -segmentPad && tRaw <= 1 + segmentPad;
    const t = clamp(tRaw, 0, 1);
    const qx = frame.x1 + t * frame.ux * frame.len;
    const qy = frame.y1 + t * frame.uy * frame.len;
    const perp = (px - qx) * frame.nx + (py - qy) * frame.ny;
    return { tRaw, t, qx, qy, perp, onSegment, frame };
}

/** Map a screen point through entry portal to the paired exit portal (tangent preserved, normal mirrored). */
export function speedrunPortalMapPointPx(
    entry: SpeedrunPortalDef,
    exit: SpeedrunPortalDef,
    px: number,
    py: number,
    rw: number,
    rh: number,
): { x: number; y: number; t: number } {
    const e = speedrunPortalClosestPx(entry, px, py, rw, rh);
    const offU = (px - e.qx) * e.frame.ux + (py - e.qy) * e.frame.uy;
    const offN = -e.perp;
    const xf = speedrunPortalFramePx(exit, rw, rh);
    const qx = xf.x1 + e.t * xf.ux * xf.len;
    const qy = xf.y1 + e.t * xf.uy * xf.len;
    return {
        x: qx + offU * xf.ux + offN * xf.nx,
        y: qy + offU * xf.uy + offN * xf.ny,
        t: e.t,
    };
}

/** Map a displacement vector from entry portal frame to exit portal frame (normal mirrored). */
export function speedrunPortalMapVectorPx(
    entry: SpeedrunPortalDef,
    exit: SpeedrunPortalDef,
    vx: number,
    vy: number,
    tPortal: number,
    rw: number,
    rh: number,
): { x: number; y: number } {
    const ef = speedrunPortalFramePx(entry, rw, rh);
    const xf = speedrunPortalFramePx(exit, rw, rh);
    const vu = vx * ef.ux + vy * ef.uy;
    const vn = vx * ef.nx + vy * ef.ny;
    return {
        x: vu * xf.ux - vn * xf.nx,
        y: vu * xf.uy - vn * xf.ny,
    };
}

export type SpeedrunPortalCrossPx = {
    entryX: number;
    entryY: number;
    exitX: number;
    exitY: number;
    outX: number;
    outY: number;
    tPortal: number;
};

/** If motion p0→p1 crosses the entry portal plane inside the gate, return warp result. */
export function speedrunPortalTryMotionCrossPx(
    p0x: number,
    p0y: number,
    p1x: number,
    p1y: number,
    entry: SpeedrunPortalDef,
    exit: SpeedrunPortalDef,
    rw: number,
    rh: number,
    gateBand: number,
): SpeedrunPortalCrossPx | null {
    const c0 = speedrunPortalClosestPx(entry, p0x, p0y, rw, rh, 0.18);
    const c1 = speedrunPortalClosestPx(entry, p1x, p1y, rw, rh, 0.18);
    const perp0 = c0.perp;
    const perp1 = c1.perp;
    const denom = perp0 - perp1;
    if (Math.abs(denom) < 1e-8) return null;

    // Crossing portal plane from either side (+n→−n or −n→+n).
    const crosses = (perp0 > 0 && perp1 <= 0) || (perp0 < 0 && perp1 >= 0);
    if (!crosses) return null;

    const u = perp0 / denom;
    if (u < 0 || u > 1) return null;
    const entryX = p0x + u * (p1x - p0x);
    const entryY = p0y + u * (p1y - p0y);
    const cCross = speedrunPortalClosestPx(entry, entryX, entryY, rw, rh, 0.18);
    if (!cCross.onSegment || Math.abs(cCross.perp) > gateBand) return null;

    const xf = speedrunPortalFramePx(exit, rw, rh);
    const exitX = xf.x1 + cCross.t * xf.ux * xf.len;
    const exitY = xf.y1 + cCross.t * xf.uy * xf.len;
    const remX = p1x - entryX;
    const remY = p1y - entryY;
    const remM = speedrunPortalMapVectorPx(entry, exit, remX, remY, cCross.t, rw, rh);
    return {
        entryX: cCross.qx,
        entryY: cCross.qy,
        exitX,
        exitY,
        outX: exitX + remM.x,
        outY: exitY + remM.y,
        tPortal: cCross.t,
    };
}

export function drawSpeedrunPortalLine(
    ctx: CanvasRenderingContext2D,
    portal: SpeedrunPortalDef,
    rw: number,
    rh: number,
    options?: { selected?: boolean; lineScale?: number },
): void {
    const color = speedrunPortalColor(portal.group, portal.side);
    const x1 = portal.ax * rw;
    const y1 = portal.ay * rh;
    const x2 = portal.bx * rw;
    const y2 = portal.by * rh;
    const ls = options?.lineScale ?? 1;
    const lw = Math.max(5, 9 * ls);
    ctx.strokeStyle = color;
    ctx.shadowColor = color;
    ctx.shadowBlur = (options?.selected ? 20 : 14) * ls;
    ctx.lineWidth = lw;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.shadowBlur = 0;
}

/** Decorative label in normalized box coords (editor + runtime). */
export type SpeedrunTextDef = {
    id?: string;
    nx: number;
    ny: number;
    nw: number;
    nh: number;
    content?: string;
    /** `#rrggbb` */
    color?: string;
    rotationDeg?: number;
};

export type SpeedrunLevelDef = {
    spawn: { nx: number; ny: number };
    goal: SpeedrunGoalDef;
    /** Walls (can include `move` / `rotate`). Plain segments are accepted. */
    staticSegs: WallSegDef[];
    moving: MovingLaserDef[];
    /** Optional collectibles — all must be touched before the goal counts. */
    points?: SpeedrunPointDef[];
    /** Optional wind zones — gentle push with direction; opposing movement is heavily damped. */
    winds?: SpeedrunWindDef[];
    /** Optional decorative text labels. */
    texts?: SpeedrunTextDef[];
    /** Optional linked portal pairs (A↔B per group). */
    portals?: SpeedrunPortalDef[];
    /** Optional deltoid checkpoints — last touched sets respawn. */
    checkpoints?: SpeedrunCheckpointDef[];
    /** @deprecated Legacy single checkpoint; migrated to `checkpoints`. */
    checkpoint?: SpeedrunCheckpointDef;
    /** Playtest start override (editor); max one. Not used in official runs. */
    temporalSpawn?: SpeedrunPlaytestCheckpointDef;
    /** @deprecated Use `temporalSpawn`. */
    playtestCheckpoint?: SpeedrunPlaytestCheckpointDef;
};

const runtimeLevels = new Map<number, SpeedrunLevelDef>();

/** Playtest / admin override for a single level slot. */
let playtestOverride: SpeedrunLevelDef | null = null;
let playtestLevelIndex = 1;

function clamp(v: number, lo: number, hi: number) {
    return Math.max(lo, Math.min(hi, v));
}

export function distPointToSeg(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len2 = dx * dx + dy * dy;
    if (len2 < 1e-10) return Math.hypot(px - x1, py - y1);
    let t = ((px - x1) * dx + (py - y1) * dy) / len2;
    t = clamp(t, 0, 1);
    const qx = x1 + t * dx;
    const qy = y1 + t * dy;
    return Math.hypot(px - qx, py - qy);
}

/** Moving circle (segment x0,y0→x1,y1 radius segR) vs static circle — prevents high-speed tunneling. */
export function sweptCircleHitsCircle(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    segR: number,
    cx: number,
    cy: number,
    circleR: number,
): boolean {
    const sumR = segR + circleR;
    return distPointToSeg(cx, cy, x0, y0, x1, y1) < sumR;
}

/** Minimum distance between finite segments (ax,ay)-(bx,by) and (cx,cy)-(dx,dy) in 2D. */
export function distSegToSeg2D(
    ax: number,
    ay: number,
    bx: number,
    by: number,
    cx: number,
    cy: number,
    dx: number,
    dy: number,
): number {
    const d1x = bx - ax;
    const d1y = by - ay;
    const d2x = dx - cx;
    const d2y = dy - cy;
    const wx = ax - cx;
    const wy = ay - cy;
    const uu = d1x * d1x + d1y * d1y;
    const vv = d2x * d2x + d2y * d2y;
    const uv = d1x * d2x + d1y * d2y;
    const uw = d1x * wx + d1y * wy;
    const vw = d2x * wx + d2y * wy;
    const EPS = 1e-14;
    let bestSq = Infinity;
    const add = (s: number, t: number) => {
        const sc = clamp(s, 0, 1);
        const tc = clamp(t, 0, 1);
        const nx = ax + sc * d1x - cx - tc * d2x;
        const ny = ay + sc * d1y - cy - tc * d2y;
        const d2v = nx * nx + ny * ny;
        if (d2v < bestSq) bestSq = d2v;
    };
    if (uu < EPS && vv < EPS) return Math.hypot(wx, wy);
    if (uu < EPS) {
        add(0, vw / vv);
        return Math.sqrt(bestSq);
    }
    if (vv < EPS) {
        add(-uw / uu, 0);
        return Math.sqrt(bestSq);
    }
    const detM = uu * vv - uv * uv;
    if (detM < EPS * Math.max(uu, vv)) {
        return Math.min(
            distPointToSeg(ax, ay, cx, cy, dx, dy),
            distPointToSeg(bx, by, cx, cy, dx, dy),
            distPointToSeg(cx, cy, ax, ay, bx, by),
            distPointToSeg(dx, dy, ax, ay, bx, by),
        );
    }
    const s0 = (uv * vw - uw * vv) / detM;
    const t0 = (uu * vw - uv * uw) / detM;
    add(s0, t0);
    add(0, vw / vv);
    add(1, (d2x * (wx + d1x) + d2y * (wy + d1y)) / vv);
    add(-uw / uu, 0);
    add(-(d1x * (wx - d2x) + d1y * (wy - d2y)) / uu, 1);
    add(0, 0);
    add(0, 1);
    add(1, 0);
    add(1, 1);
    return Math.sqrt(bestSq);
}

/** Circle (cx,cy,r) overlaps axis-aligned rect (top-left rx,ry size rw×rh). */
export function circleRectOverlap(cx: number, cy: number, r: number, rx: number, ry: number, rw: number, rh: number): boolean {
    const qx = clamp(cx, rx, rx + rw);
    const qy = clamp(cy, ry, ry + rh);
    return Math.hypot(cx - qx, cy - qy) < r;
}

/** Factor 0 = at rest, 1 = at target; continuous oscillation like `move` on walls. */
export function speedrunOscMoveLerp(tSec: number, outS: number, backS: number): number {
    const o = Math.max(outS, 0.05);
    const b = Math.max(backS, 0.05);
    const T = o + b;
    const tt = tSec % T;
    return tt < o ? tt / o : 1 - (tt - o) / b;
}

export function speedrunPosWithOscMove(
    nx: number,
    ny: number,
    move: OscPointMove | undefined,
    tSec: number,
): { nx: number; ny: number } {
    if (!move || move.outS < 0.01 || move.backS < 0.01) return { nx, ny };
    const l = speedrunOscMoveLerp(tSec, move.outS, move.backS);
    return {
        nx: clamp(nx + (move.tnx - nx) * l, 0, 1),
        ny: clamp(ny + (move.tny - ny) * l, 0, 1),
    };
}

function lerpNorm2(
    a: { nx: number; ny: number },
    b: { nx: number; ny: number },
    u: number,
): { nx: number; ny: number } {
    return {
        nx: a.nx + (b.nx - a.nx) * u,
        ny: a.ny + (b.ny - a.ny) * u,
    };
}

function walkPolylineAt(
    pts: { nx: number; ny: number }[],
    elapsed: number,
    legDur: number,
): { nx: number; ny: number } {
    const nLeg = pts.length - 1;
    if (nLeg <= 0) return pts[0];
    const leg = Math.min(nLeg - 1, Math.floor(elapsed / legDur));
    const u = clamp((elapsed - leg * legDur) / legDur, 0, 1);
    return lerpNorm2(pts[leg], pts[leg + 1], u);
}

/** Centroid position of the `pathMove` path in normalized 0–1 coordinates. */
export function oscPathCentroidNorm(pm: OscPathMove, tSec: number): { nx: number; ny: number } {
    const o = Math.max(pm.legOutS, 0.05);
    const b = Math.max(pm.legBackS, 0.05);
    const R = { nx: pm.restNx, ny: pm.restNy };
    const A = pm.anchors;
    if (!A.length) return R;
    const fwd = [R, ...A];
    const M = A.length;
    const Tfwd = M * o;
    if (pm.loop) {
        const Tret = b;
        const T = Tfwd + Tret;
        const tt = tSec % T;
        if (tt < Tfwd) return walkPolylineAt(fwd, tt, o);
        const u = clamp((tt - Tfwd) / Tret, 0, 1);
        const last = A[M - 1];
        return lerpNorm2(last, R, u);
    }
    const Tback = M * b;
    const T = Tfwd + Tback;
    const tt = tSec % T;
    if (tt < Tfwd) return walkPolylineAt(fwd, tt, o);
    const rev = [...fwd].reverse();
    return walkPolylineAt(rev, tt - Tfwd, b);
}

export function speedrunPointGoalPosNorm(
    nx: number,
    ny: number,
    pathMove: OscPathMove | undefined,
    move: OscPointMove | undefined,
    tSec: number,
): { nx: number; ny: number } {
    if (pathMove && pathMove.anchors.length) {
        const G = oscPathCentroidNorm(pathMove, tSec);
        return {
            nx: clamp(nx + G.nx - pathMove.restNx, 0, 1),
            ny: clamp(ny + G.ny - pathMove.restNy, 0, 1),
        };
    }
    return speedrunPosWithOscMove(nx, ny, move, tSec);
}

/** Moving laser segment in normalized 0–1 coordinates (includes `move` when set). */
export function movingLaserSegNorm(d: MovingLaserDef, tSec: number): NormSeg {
    let baseY = d.y;
    let baseCx = d.cx ?? 0.5;
    if (d.pathMove && d.pathMove.anchors.length) {
        const G = oscPathCentroidNorm(d.pathMove, tSec);
        baseCx = clamp(baseCx + (G.nx - d.pathMove.restNx), 0, 1);
        baseY = clamp(baseY + (G.ny - d.pathMove.restNy), 0, 1);
    }
    let y = baseY;
    let cx = baseCx;
    const mv = d.move;
    if (mv && mv.outS > 0.01 && mv.backS > 0.01) {
        const l = speedrunOscMoveLerp(tSec, mv.outS, mv.backS);
        y = clamp(baseY + (mv.ty - baseY) * l, 0, 1);
        cx = clamp(baseCx + (mv.tcx - baseCx) * l, 0, 1);
    }
    const cN = cx + d.amp * Math.sin(tSec * d.speed + d.phase);
    const yn = y;
    const hl = d.halfLen;
    return {
        ax: clamp(cN - hl, 0, 1),
        ay: clamp(yn, 0, 1),
        bx: clamp(cN + hl, 0, 1),
        by: clamp(yn, 0, 1),
    };
}

const STUB_LEVEL: SpeedrunLevelDef = {
    spawn: { nx: 0.5, ny: 0.92 },
    goal: { nx: 0.5, ny: 0.08, nHalf: 0.035 },
    staticSegs: [],
    moving: [],
    points: [],
};

const WIND_DIRS: readonly SpeedrunWindDir[] = ["up", "down", "left", "right"] as const;

function parseSpeedrunWindDir(raw: unknown): SpeedrunWindDir {
    if (typeof raw === "string" && (WIND_DIRS as readonly string[]).includes(raw)) return raw as SpeedrunWindDir;
    return "right";
}

function parsePortalDef(raw: unknown): SpeedrunPortalDef | null {
    if (!raw || typeof raw !== "object") return null;
    const r = raw as Record<string, unknown>;
    const group = Math.round(Number(r.group));
    if (!Number.isFinite(group) || group < 1 || group > 10) return null;
    const sideRaw = String(r.side ?? r.end ?? "a").toLowerCase();
    const side: SpeedrunPortalSide = sideRaw === "b" ? "b" : "a";
    const ax = Number(r.ax);
    const ay = Number(r.ay);
    const bx = Number(r.bx);
    const by = Number(r.by);
    if (![ax, ay, bx, by].every(Number.isFinite)) return null;
    if (Math.hypot(bx - ax, by - ay) < 0.003) return null;
    const o: SpeedrunPortalDef = {
        group,
        side,
        ax: clamp(ax, 0, 1),
        ay: clamp(ay, 0, 1),
        bx: clamp(bx, 0, 1),
        by: clamp(by, 0, 1),
    };
    if (typeof r.id === "string") o.id = r.id;
    return o;
}

function parseWindDef(raw: unknown): SpeedrunWindDef | null {
    if (!raw || typeof raw !== "object") return null;
    const r = raw as Record<string, unknown>;
    const nx = Number(r.nx);
    const ny = Number(r.ny);
    const nw = Number(r.nw);
    const nh = Number(r.nh);
    if (![nx, ny, nw, nh].every(Number.isFinite)) return null;
    if (nw < 0.004 || nh < 0.004) return null;
    const o: SpeedrunWindDef = {
        nx: clamp(nx, 0, 1),
        ny: clamp(ny, 0, 1),
        nw: clamp(nw, 0.004, 1),
        nh: clamp(nh, 0.004, 1),
        dir: parseSpeedrunWindDir(r.dir),
    };
    if (o.nx + o.nw > 1) o.nw = 1 - o.nx;
    if (o.ny + o.nh > 1) o.nh = 1 - o.ny;
    if (typeof r.id === "string") o.id = r.id;
    const st = Number(r.strength);
    if (Number.isFinite(st)) {
        const sc = clamp(st, 0.15, 50);
        if (Math.abs(sc - 1) > 1e-6) o.strength = sc;
    }
    return o;
}

function parseTextDef(raw: unknown): SpeedrunTextDef | null {
    if (!raw || typeof raw !== "object") return null;
    const r = raw as Record<string, unknown>;
    const nx = Number(r.nx);
    const ny = Number(r.ny);
    const nw = Number(r.nw);
    const nh = Number(r.nh);
    if (![nx, ny, nw, nh].every(Number.isFinite)) return null;
    if (nw < 0.012 || nh < 0.012) return null;
    const o: SpeedrunTextDef = {
        nx: clamp(nx, 0, 1),
        ny: clamp(ny, 0, 1),
        nw: clamp(nw, 0.012, 1),
        nh: clamp(nh, 0.012, 1),
    };
    if (o.nx + o.nw > 1) o.nw = 1 - o.nx;
    if (o.ny + o.nh > 1) o.nh = 1 - o.ny;
    if (typeof r.id === "string") o.id = r.id.slice(0, 64);
    if (r.content != null) o.content = String(r.content).slice(0, 500);
    const col = String(r.color || "").trim();
    if (/^#[0-9a-fA-F]{6}$/.test(col)) o.color = col.toLowerCase();
    const rot = Number(r.rotationDeg);
    if (Number.isFinite(rot)) o.rotationDeg = rot;
    return o;
}

/** Effective wind strength for runtime / editor (defaults to 1). */
export function speedrunWindStrengthValue(w: SpeedrunWindDef): number {
    const s = Number(w.strength);
    if (!Number.isFinite(s)) return 1;
    return clamp(s, 0.15, 50);
}

/** Unit vector in pixel space (+y down): cursor is pushed this way when inside the zone. */
export function speedrunWindDirUnit(dir: SpeedrunWindDir): { x: number; y: number } {
    switch (dir) {
        case "up":
            return { x: 0, y: -1 };
        case "down":
            return { x: 0, y: 1 };
        case "left":
            return { x: -1, y: 0 };
        case "right":
            return { x: 1, y: 0 };
        default:
            return { x: 1, y: 0 };
    }
}

/** Normalized point inside wind AABB (inclusive edges). */
export function normPointInWindZone(px: number, py: number, w: SpeedrunWindDef): boolean {
    return px >= w.nx && px <= w.nx + w.nw && py >= w.ny && py <= w.ny + w.nh;
}

function parseOscPathMove(raw: unknown): OscPathMove | undefined {
    if (!raw || typeof raw !== "object") return undefined;
    const r = raw as Record<string, unknown>;
    const restNx = Number(r.restNx);
    const restNy = Number(r.restNy);
    const legOutS = Number(r.legOutS);
    const legBackS = Number(r.legBackS);
    if (![restNx, restNy, legOutS, legBackS].every(Number.isFinite)) return undefined;
    const loop = r.loop === true;
    const anchors: { nx: number; ny: number }[] = [];
    const arr = r.anchors;
    if (Array.isArray(arr)) {
        for (const w of arr) {
            if (!w || typeof w !== "object") continue;
            const wr = w as Record<string, unknown>;
            const anx = Number(wr.nx);
            const any = Number(wr.ny);
            if (Number.isFinite(anx) && Number.isFinite(any)) {
                anchors.push({ nx: clamp(anx, 0, 1), ny: clamp(any, 0, 1) });
            }
        }
    }
    if (!anchors.length) return undefined;
    return {
        restNx: clamp(restNx, 0, 1),
        restNy: clamp(restNy, 0, 1),
        anchors,
        loop,
        legOutS: clamp(legOutS, 0.05, 120),
        legBackS: clamp(legBackS, 0.05, 120),
    };
}

function parseWallSeg(raw: Record<string, unknown>): WallSegDef | null {
    const ax = Number(raw.ax);
    const ay = Number(raw.ay);
    const bx = Number(raw.bx);
    const by = Number(raw.by);
    if (![ax, ay, bx, by].every(Number.isFinite)) return null;
    const o: WallSegDef = {
        ax: clamp(ax, 0, 1),
        ay: clamp(ay, 0, 1),
        bx: clamp(bx, 0, 1),
        by: clamp(by, 0, 1),
    };
    if (Math.hypot(o.bx - o.ax, o.by - o.ay) < 0.0002) return null;
    if (typeof raw.id === "string") o.id = raw.id;
    const tg = parseToggleGroupField(raw.toggleGroup);
    if (tg != null) o.toggleGroup = tg;
    const tpt = parsePointTypeField(raw.togglePointType);
    if (tpt != null && tpt !== "circle") o.togglePointType = tpt;
    const mv = raw.move;
    if (mv && typeof mv === "object") {
        const m = mv as Record<string, unknown>;
        const tax = Number(m.tax);
        const tay = Number(m.tay);
        const tbx = Number(m.tbx);
        const tby = Number(m.tby);
        const outS = Number(m.outS);
        const backS = Number(m.backS);
        if ([tax, tay, tbx, tby, outS, backS].every(Number.isFinite) && outS > 0 && backS > 0) {
            o.move = {
                tax: clamp(tax, -0.2, 1.2),
                tay: clamp(tay, -0.2, 1.2),
                tbx: clamp(tbx, -0.2, 1.2),
                tby: clamp(tby, -0.2, 1.2),
                outS: clamp(outS, 0.05, 120),
                backS: clamp(backS, 0.05, 120),
            };
        }
    }
    const pthWall = parseOscPathMove(raw.pathMove);
    if (pthWall) o.pathMove = pthWall;
    const rot = raw.rotate;
    if (rot && typeof rot === "object") {
        const r = rot as Record<string, unknown>;
        const deg = Number(r.deg);
        const periodS = Number(r.periodS);
        if (Number.isFinite(deg) && Number.isFinite(periodS) && periodS > 0.05) {
            const base = { deg: clamp(deg, -720, 720), periodS: clamp(periodS, 0.05, 120) };
            if (r.infinite === true) {
                const sd = Number(r.spinDir);
                o.rotate = {
                    ...base,
                    infinite: true,
                    spinDir: sd === -1 ? -1 : 1,
                };
            } else {
                o.rotate = base;
            }
        }
    }
    const lhmW = Number(raw.lineHalfMul);
    if (Number.isFinite(lhmW)) o.lineHalfMul = clamp(lhmW, 0.25, 5);
    if (raw.solidWall === true) o.solidWall = true;
    return o;
}

function parseOscPointMove(raw: Record<string, unknown>): OscPointMove | undefined {
    const m = raw.move;
    if (!m || typeof m !== "object") return undefined;
    const mo = m as Record<string, unknown>;
    const tnx = Number(mo.tnx);
    const tny = Number(mo.tny);
    const outS = Number(mo.outS);
    const backS = Number(mo.backS);
    if (![tnx, tny, outS, backS].every(Number.isFinite)) return undefined;
    return {
        tnx: clamp(tnx, 0, 1),
        tny: clamp(tny, 0, 1),
        outS: clamp(outS, 0.05, 120),
        backS: clamp(backS, 0.05, 120),
    };
}

function parseMoving(raw: Record<string, unknown>): MovingLaserDef | null {
    const y = Number(raw.y);
    const halfLen = Number(raw.halfLen);
    const amp = Number(raw.amp);
    const speed = Number(raw.speed);
    const phase = Number(raw.phase);
    if (![y, halfLen, amp, speed, phase].every(Number.isFinite)) return null;
    const cxRaw = Number(raw.cx);
    const o: MovingLaserDef = {
        y: clamp(y, 0, 1),
        halfLen: clamp(halfLen, 0.01, 0.5),
        amp: clamp(amp, 0, 0.5),
        speed: clamp(speed, 0.05, 8),
        phase,
    };
    if (Number.isFinite(cxRaw)) o.cx = clamp(cxRaw, 0, 1);
    const mtg = parseToggleGroupField(raw.toggleGroup);
    if (mtg != null) o.toggleGroup = mtg;
    const mtpt = parsePointTypeField(raw.togglePointType);
    if (mtpt != null && mtpt !== "circle") o.togglePointType = mtpt;
    const lmove = raw.move;
    if (lmove && typeof lmove === "object") {
        const mo = lmove as Record<string, unknown>;
        if (!Number.isFinite(Number(mo.tax))) {
            const ty = Number(mo.ty);
            const tcx = Number(mo.tcx);
            const outS = Number(mo.outS);
            const backS = Number(mo.backS);
            if ([ty, tcx, outS, backS].every(Number.isFinite)) {
                o.move = {
                    ty: clamp(ty, 0, 1),
                    tcx: clamp(tcx, 0, 1),
                    outS: clamp(outS, 0.05, 120),
                    backS: clamp(backS, 0.05, 120),
                };
            }
        }
    }
    const pthM = parseOscPathMove(raw.pathMove);
    if (pthM) o.pathMove = pthM;
    const lhmM = Number(raw.lineHalfMul);
    if (Number.isFinite(lhmM)) o.lineHalfMul = clamp(lhmM, 0.25, 5);
    return o;
}

/** Unwrap DB / API payloads (JSON string, nested `{ data }`, legacy shapes). */
function unwrapSpeedrunLevelPayload(raw: unknown): unknown {
    if (typeof raw === "string") {
        try {
            return unwrapSpeedrunLevelPayload(JSON.parse(raw));
        } catch {
            return null;
        }
    }
    if (!raw || typeof raw !== "object") return raw;
    const o = raw as Record<string, unknown>;
    const nested = o.data;
    const hasTopLevel =
        o.spawn != null ||
        o.goal != null ||
        o.staticSegs != null ||
        o.segs != null ||
        o.moving != null ||
        o.portals != null ||
        o.points != null ||
        o.checkpoints != null ||
        o.checkpoint != null;
    if (!hasTopLevel && nested != null && typeof nested === "object") {
        return unwrapSpeedrunLevelPayload(nested);
    }
    return raw;
}

function speedrunPayloadLooksValid(raw: unknown): boolean {
    const unwrapped = unwrapSpeedrunLevelPayload(raw);
    if (!unwrapped || typeof unwrapped !== "object") return false;
    const o = unwrapped as Record<string, unknown>;
    const spawn = o.spawn;
    const goal = o.goal;
    if (!spawn || typeof spawn !== "object" || !goal || typeof goal !== "object") return false;
    const sn = spawn as Record<string, unknown>;
    const gn = goal as Record<string, unknown>;
    return (
        Number.isFinite(Number(sn.nx)) &&
        Number.isFinite(Number(sn.ny)) &&
        Number.isFinite(Number(gn.nx)) &&
        Number.isFinite(Number(gn.ny))
    );
}

/** Normalize JSON from DB or editor into a runtime def. */
export function normalizeLevelData(raw: unknown): SpeedrunLevelDef {
    const unwrapped = unwrapSpeedrunLevelPayload(raw);
    const o = unwrapped && typeof unwrapped === "object" ? (unwrapped as Record<string, unknown>) : {};
    const segsIn = o.staticSegs ?? o.segs;
    const staticSegs: WallSegDef[] = [];
    if (Array.isArray(segsIn)) {
        for (const s of segsIn) {
            if (!s || typeof s !== "object") continue;
            const w = parseWallSeg(s as Record<string, unknown>);
            if (w) staticSegs.push(w);
        }
    }
    const moving: MovingLaserDef[] = [];
    if (Array.isArray(o.moving)) {
        for (const m of o.moving) {
            if (!m || typeof m !== "object") continue;
            const mv = parseMoving(m as Record<string, unknown>);
            if (mv) moving.push(mv);
        }
    }
    const spawn = o.spawn && typeof o.spawn === "object" ? (o.spawn as Record<string, unknown>) : {};
    const goal = o.goal && typeof o.goal === "object" ? (o.goal as Record<string, unknown>) : {};
    let nHalf = Number(goal.nHalf);
    if (!Number.isFinite(nHalf)) nHalf = STUB_LEVEL.goal.nHalf;
    nHalf = clamp(nHalf, 0.015, 0.14);
    const points: SpeedrunPointDef[] = [];
    if (Array.isArray(o.points)) {
        for (const p of o.points) {
            if (!p || typeof p !== "object") continue;
            const pr = p as Record<string, unknown>;
            const nx = Number(pr.nx);
            const ny = Number(pr.ny);
            if (Number.isFinite(nx) && Number.isFinite(ny)) {
                const pt: SpeedrunPointDef = { nx: clamp(nx, 0, 1), ny: clamp(ny, 0, 1) };
                const pg = parseToggleGroupField(pr.group);
                if (pg != null) pt.group = pg;
                const ptype = parsePointTypeField(pr.pointType);
                if (ptype != null && ptype !== "circle") pt.pointType = ptype;
                const pm = parseOscPointMove(pr);
                if (pm) pt.move = pm;
                const ppath = parseOscPathMove(pr.pathMove);
                if (ppath) pt.pathMove = ppath;
                const rm = Number(pr.radiusMul);
                if (Number.isFinite(rm)) pt.radiusMul = clamp(rm, 0.35, 3);
                const prd = Number(pr.rotationDeg);
                if (Number.isFinite(prd)) pt.rotationDeg = prd;
                points.push(pt);
            }
        }
    }
    let checkpoints: SpeedrunCheckpointDef[] = [];
    const parseCk = (cpr: Record<string, unknown>): SpeedrunCheckpointDef | null => {
        const cnx = Number(cpr.nx);
        const cny = Number(cpr.ny);
        if (!Number.isFinite(cnx) || !Number.isFinite(cny)) return null;
        const ck: SpeedrunCheckpointDef = { nx: clamp(cnx, 0, 1), ny: clamp(cny, 0, 1) };
        const sm = Number(cpr.sizeMul);
        if (Number.isFinite(sm)) ck.sizeMul = clamp(sm, 0.45, 2.5);
        const rd = Number(cpr.rotationDeg);
        if (Number.isFinite(rd)) ck.rotationDeg = rd;
        const cm = parseOscPointMove(cpr);
        if (cm) ck.move = cm;
        const cpath = parseOscPathMove(cpr.pathMove);
        if (cpath) ck.pathMove = cpath;
        return ck;
    };
    if (Array.isArray(o.checkpoints)) {
        for (const c of o.checkpoints) {
            if (!c || typeof c !== "object") continue;
            const parsed = parseCk(c as Record<string, unknown>);
            if (parsed) checkpoints.push(parsed);
        }
    } else if (o.checkpoint && typeof o.checkpoint === "object" && !Array.isArray(o.checkpoint)) {
        const parsed = parseCk(o.checkpoint as Record<string, unknown>);
        if (parsed) checkpoints = [parsed];
    }
    let temporalSpawn: SpeedrunPlaytestCheckpointDef | undefined;
    const tsRaw = o.temporalSpawn ?? o.playtestCheckpoint;
    if (tsRaw && typeof tsRaw === "object") {
        const parsed = parseCk(tsRaw as Record<string, unknown>);
        if (parsed) temporalSpawn = parsed;
    }
    const goalOut: SpeedrunGoalDef = {
        nx: clamp(Number(goal.nx), 0, 1) || STUB_LEVEL.goal.nx,
        ny: clamp(Number(goal.ny), 0, 1) || STUB_LEVEL.goal.ny,
        nHalf,
    };
    const gm = parseOscPointMove(goal);
    if (gm) goalOut.move = gm;
    const gpath = parseOscPathMove(goal.pathMove);
    if (gpath) goalOut.pathMove = gpath;
    const grd = Number(goal.rotationDeg);
    if (Number.isFinite(grd)) goalOut.rotationDeg = grd;
    const winds: SpeedrunWindDef[] = [];
    if (Array.isArray(o.winds)) {
        for (const w of o.winds) {
            if (!w || typeof w !== "object") continue;
            const wd = parseWindDef(w);
            if (wd) winds.push(wd);
        }
    }
    const texts: SpeedrunTextDef[] = [];
    if (Array.isArray(o.texts)) {
        for (const t of o.texts) {
            const td = parseTextDef(t);
            if (td) texts.push(td);
        }
    }
    const portals: SpeedrunPortalDef[] = [];
    if (Array.isArray(o.portals)) {
        for (const p of o.portals) {
            const pd = parsePortalDef(p);
            if (pd) portals.push(pd);
        }
    }

    const out: SpeedrunLevelDef = {
        spawn: {
            nx: clamp(Number(spawn.nx), 0, 1) || STUB_LEVEL.spawn.nx,
            ny: clamp(Number(spawn.ny), 0, 1) || STUB_LEVEL.spawn.ny,
        },
        goal: goalOut,
        staticSegs,
        moving,
        points: points.length ? points : undefined,
        winds: winds.length ? winds : undefined,
        texts: texts.length ? texts : undefined,
        portals: portals.length ? portals : undefined,
    };
    if (checkpoints.length) out.checkpoints = checkpoints;
    if (temporalSpawn) out.temporalSpawn = temporalSpawn;
    return out;
}

export type SpeedrunLevelPayload = {
    levelIndex: number;
    data: unknown;
    displayName?: string | null;
    description?: string | null;
    authorName?: string | null;
    viewCount?: number;
};

export type SpeedrunLevelCardMeta = {
    displayName?: string | null;
    description?: string | null;
    authorName?: string | null;
    viewCount: number;
};

const runtimeLevelMeta = new Map<number, SpeedrunLevelCardMeta>();

export function setRuntimeSpeedrunLevelMetaFromPayload(
    levels: Array<{
        levelIndex: number;
        displayName?: string | null;
        description?: string | null;
        authorName?: string | null;
        viewCount?: number;
    }>,
) {
    runtimeLevelMeta.clear();
    for (const row of levels) {
        const idx = Math.floor(Number(row.levelIndex));
        if (!Number.isInteger(idx) || idx < 1) continue;
        runtimeLevelMeta.set(idx, {
            displayName: row.displayName ?? null,
            description: row.description ?? null,
            authorName: row.authorName ?? null,
            viewCount: Math.max(0, Math.floor(Number(row.viewCount) || 0)),
        });
    }
}

export function getSpeedrunLevelCardMeta(level: number): SpeedrunLevelCardMeta | null {
    return runtimeLevelMeta.get(Math.floor(level)) ?? null;
}

export function getSpeedrunLevelDisplayTitle(level: number): string {
    const meta = getSpeedrunLevelCardMeta(level);
    const name = meta?.displayName?.trim();
    return name || `Level ${Math.floor(level)}`;
}

export function bumpSpeedrunLevelViewCountLocal(level: number, viewCount: number): void {
    const idx = Math.floor(level);
    const prev = runtimeLevelMeta.get(idx);
    if (prev) runtimeLevelMeta.set(idx, { ...prev, viewCount: Math.max(0, Math.floor(viewCount)) });
}

export function setRuntimeSpeedrunLevelsFromPayload(levels: SpeedrunLevelPayload[]) {
    runtimeLevels.clear();
    for (const row of levels) {
        const idx = Math.floor(Number(row.levelIndex));
        if (!Number.isInteger(idx) || idx < 1) continue;
        if (!speedrunPayloadLooksValid(row.data)) continue;
        runtimeLevels.set(idx, normalizeLevelData(row.data));
    }
    setRuntimeSpeedrunLevelMetaFromPayload(levels);
}

/** Official level indices present in runtime (sorted). */
export function getSpeedrunPublishedLevelIndices(): number[] {
    return [...runtimeLevels.keys()].sort((a, b) => a - b);
}

export function hasSpeedrunPublishedLevel(level: number): boolean {
    const lv = Math.floor(level);
    return Number.isInteger(lv) && lv >= 1 && runtimeLevels.has(lv);
}

export function getNextSpeedrunPublishedLevel(afterLevel: number): number | null {
    const lv = Math.floor(afterLevel);
    for (const idx of getSpeedrunPublishedLevelIndices()) {
        if (idx > lv) return idx;
    }
    return null;
}

export function getSpeedrunLevelCount(): number {
    if (runtimeLevels.size === 0) return 0;
    return Math.max(...runtimeLevels.keys());
}

export function getSpeedrunLevelDef(level: number): SpeedrunLevelDef {
    const lv = Math.floor(level);
    if (playtestOverride && lv === playtestLevelIndex) return playtestOverride;
    const d = runtimeLevels.get(lv);
    if (d) return d;
    return STUB_LEVEL;
}

export function setSpeedrunPlaytestOverride(levelIndex: number, def: SpeedrunLevelDef | null) {
    playtestLevelIndex = Math.max(1, Math.floor(levelIndex));
    playtestOverride = def;
}

export function clearSpeedrunPlaytestOverride() {
    playtestOverride = null;
}

/** Pixel segment at time `tSec` (translation then rotation around midpoint). */
export function wallSegToPx(s: WallSegDef, tSec: number, w: number, h: number): { x1: number; y1: number; x2: number; y2: number } {
    let ax = s.ax * w;
    let ay = s.ay * h;
    let bx = s.bx * w;
    let by = s.by * h;

    const pm = s.pathMove;
    if (pm && pm.anchors.length) {
        const G = oscPathCentroidNorm(pm, tSec);
        const dnx = G.nx - pm.restNx;
        const dny = G.ny - pm.restNy;
        ax = (s.ax + dnx) * w;
        ay = (s.ay + dny) * h;
        bx = (s.bx + dnx) * w;
        by = (s.by + dny) * h;
    }

    const mv = s.move;
    if (mv && mv.outS > 0.01 && mv.backS > 0.01 && !(pm && pm.anchors.length)) {
        const oax = ax;
        const oay = ay;
        const obx = bx;
        const oby = by;
        const tax = mv.tax * w;
        const tay = mv.tay * h;
        const tbx = mv.tbx * w;
        const tby = mv.tby * h;
        const l = speedrunOscMoveLerp(tSec, mv.outS, mv.backS);
        ax = oax + (tax - oax) * l;
        ay = oay + (tay - oay) * l;
        bx = obx + (tbx - obx) * l;
        by = oby + (tby - oby) * l;
    }

    const rot = s.rotate;
    if (rot && rot.periodS > 0.05) {
        const midx = (ax + bx) / 2;
        const midy = (ay + by) / 2;
        let ang: number;
        if (rot.infinite) {
            const sd = rot.spinDir === -1 ? -1 : 1;
            ang = (sd * 2 * Math.PI * tSec) / rot.periodS;
        } else {
            ang = ((Math.sin((2 * Math.PI * tSec) / rot.periodS) + 1) / 2) * (rot.deg * (Math.PI / 180));
        }
        const c = Math.cos(ang);
        const sn = Math.sin(ang);
        const rax = ax - midx;
        const ray = ay - midy;
        const rbx = bx - midx;
        const rby = by - midy;
        ax = midx + rax * c - ray * sn;
        ay = midy + rax * sn + ray * c;
        bx = midx + rbx * c - rby * sn;
        by = midy + rbx * sn + rby * c;
    }

    return { x1: ax, y1: ay, x2: bx, y2: by };
}

export function wallDrawAlpha(s: WallSegDef, hideMoveVis: boolean, hideRotVis: boolean): number {
    let a = 1;
    if (hideMoveVis && (s.move || (s.pathMove && s.pathMove.anchors.length))) a *= 0.18;
    if (hideRotVis && s.rotate) a *= 0.18;
    return clamp(a, 0.08, 1);
}

export function normPointInTextZone(nx: number, ny: number, t: SpeedrunTextDef): boolean {
    return nx >= t.nx && nx <= t.nx + t.nw && ny >= t.ny && ny <= t.ny + t.nh;
}

/** Draw a text label inside its normalized box (runtime + editor). */
export function drawSpeedrunTextLabel(
    ctx: CanvasRenderingContext2D,
    t: SpeedrunTextDef,
    rw: number,
    rh: number,
) {
    const x = t.nx * rw;
    const y = t.ny * rh;
    const w = t.nw * rw;
    const h = t.nh * rh;
    if (w < 2 || h < 2) return;
    const cx = x + w / 2;
    const cy = y + h / 2;
    const rot = ((t.rotationDeg ?? 0) * Math.PI) / 180;
    const color = t.color && /^#[0-9a-fA-F]{6}$/.test(t.color) ? t.color : "#ffffff";
    const content = (t.content ?? "").trim() || "Text";
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rot);
    let fontPx = Math.max(8, Math.floor(h * 0.55));
    ctx.font = `${fontPx}px 'Press Start 2P', monospace`;
    while (fontPx > 6 && ctx.measureText(content).width > w * 0.92) {
        fontPx -= 1;
        ctx.font = `${fontPx}px 'Press Start 2P', monospace`;
    }
    ctx.fillStyle = color;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(content, 0, 0);
    ctx.restore();
}

/** Rotate-wheel handle position for editor (handle at top when rotation = 0°). */
export function speedrunTextRotateHandlePx(
    t: SpeedrunTextDef,
    rw: number,
    rh: number,
    paddingPx = 18,
): { cx: number; cy: number; wheelR: number; hx: number; hy: number } {
    const x = t.nx * rw;
    const y = t.ny * rh;
    const w = t.nw * rw;
    const h = t.nh * rh;
    const cx = x + w / 2;
    const cy = y + h / 2;
    const wheelR = Math.max(w, h) * 0.5 + paddingPx;
    const rad = ((t.rotationDeg ?? 0) * Math.PI) / 180 - Math.PI / 2;
    return {
        cx,
        cy,
        wheelR,
        hx: cx + Math.cos(rad) * wheelR,
        hy: cy + Math.sin(rad) * wheelR,
    };
}
